import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Rnd } from 'react-rnd';
import { ArrowLeft, Camera, TextT, Image as ImageIcon, FloppyDisk, Trash, Copy, Lock, LockOpen, Eye, ArrowsOutCardinal } from '@phosphor-icons/react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const FONTS = ['Inter', 'Playfair Display', 'Georgia', 'Courier New', 'Arial Black'];
const PRESETS_PORTRAIT = {
  strip: { photos: [{ x: 10, y: 10, w: 180, h: 160 }, { x: 10, y: 180, w: 180, h: 160 }, { x: 10, y: 350, w: 180, h: 160 }], texts: [{ text: 'Event Name', x: 100, y: 555, fontSize: 16, fontFamily: 'Playfair Display', color: '#000000' }] },
  grid: { photos: [{ x: 15, y: 15, w: 180, h: 270 }, { x: 205, y: 15, w: 180, h: 270 }, { x: 110, y: 300, w: 180, h: 270 }], texts: [{ text: 'Event Name', x: 200, y: 588, fontSize: 22, fontFamily: 'Inter', color: '#000000' }] },
  polaroid: { photos: [{ x: 25, y: 25, w: 300, h: 300 }], texts: [{ text: 'Event Name', x: 175, y: 385, fontSize: 20, fontFamily: 'Georgia', color: '#333333' }] },
  fullframe: { photos: [{ x: 15, y: 15, w: 370, h: 540 }], texts: [{ text: 'Event Name', x: 200, y: 580, fontSize: 18, fontFamily: 'Inter', color: '#FFFFFF' }] },
};
const PRESETS_LANDSCAPE = {
  strip: { photos: [{ x: 10, y: 10, w: 186, h: 180 }, { x: 204, y: 10, w: 186, h: 180 }, { x: 398, y: 10, w: 186, h: 180 }], texts: [{ text: 'Event Name', x: 300, y: 198, fontSize: 0, fontFamily: 'Playfair Display', color: '#000000' }] },
  grid: { photos: [{ x: 15, y: 15, w: 185, h: 170 }, { x: 210, y: 15, w: 185, h: 170 }, { x: 113, y: 195, w: 185, h: 170 }], texts: [{ text: 'Event Name', x: 300, y: 390, fontSize: 22, fontFamily: 'Inter', color: '#000000' }] },
  polaroid: { photos: [{ x: 60, y: 25, w: 300, h: 260 }], texts: [{ text: 'Event Name', x: 210, y: 325, fontSize: 20, fontFamily: 'Georgia', color: '#333333' }] },
  fullframe: { photos: [{ x: 15, y: 15, w: 570, h: 350 }], texts: [{ text: 'Event Name', x: 300, y: 385, fontSize: 18, fontFamily: 'Inter', color: '#FFFFFF' }] },
};

const DIMS = {
  portrait: { w: 400, h: 600 },   // 6x4 portrait (2:3)
  landscape: { w: 600, h: 400 },   // 6x4 landscape (3:2)
};
// Strip override: 6x2 ratio
const STRIP_DIMS = {
  portrait: { w: 200, h: 600 },    // 6x2 portrait (1:3)
  landscape: { w: 600, h: 200 },   // 6x2 landscape (3:1)
};
// Polaroid: ~5:6 with white border
const POLAROID_DIMS = {
  portrait: { w: 350, h: 420 },
  landscape: { w: 420, h: 350 },
};

const TemplateEditor = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [event, setEvent] = useState(null);
  const [photos, setPhotos] = useState([]);
  const [texts, setTexts] = useState([]);
  const [bgColor, setBgColor] = useState('#FFFFFF');
  const [bgImage, setBgImage] = useState(null);
  const [orientation, setOrientation] = useState('portrait'); // portrait | landscape
  const [activePreset, setActivePreset] = useState('strip');
  const [selected, setSelected] = useState(null); // { type: 'photo'|'text', index }
  const [showPreview, setShowPreview] = useState(false);
  const [saving, setSaving] = useState(false);
  const canvasRef = useRef(null);
  const fileRef = useRef(null);

  const CANVAS_W = activePreset === 'strip' ? STRIP_DIMS[orientation].w
    : activePreset === 'polaroid' ? POLAROID_DIMS[orientation].w
    : DIMS[orientation].w;
  const CANVAS_H = activePreset === 'strip' ? STRIP_DIMS[orientation].h
    : activePreset === 'polaroid' ? POLAROID_DIMS[orientation].h
    : DIMS[orientation].h;

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/events/slug/${slug}`);
        setEvent(r.data);
        // Load saved template
        try {
          const c = await axios.get(`${API}/events/${r.data.event_id}/template`);
          if (c.data?.photos) {
            const tc = c.data;
            if (tc.photos) setPhotos(tc.photos);
            if (tc.texts) setTexts(tc.texts);
            if (tc.bgColor) setBgColor(tc.bgColor);
            if (tc.bgImage) setBgImage(tc.bgImage);
            if (tc.orientation) setOrientation(tc.orientation);
            if (tc.activePreset) setActivePreset(tc.activePreset);
          } else {
            applyPreset('strip', r.data.event_name, 'portrait');
          }
        } catch { applyPreset('strip', r.data.event_name, 'portrait'); }
      } catch { navigate(-1); }
    })();
  }, [slug]);

  const applyPreset = (id, eventName, orient) => {
    const presets = (orient || orientation) === 'landscape' ? PRESETS_LANDSCAPE : PRESETS_PORTRAIT;
    const p = presets[id];
    if (!p) return;
    setActivePreset(id);
    setPhotos(p.photos.map(ph => ({ ...ph, id: Math.random().toString(36).slice(2, 8), locked: false })));
    setTexts(p.texts.map(t => ({ ...t, id: Math.random().toString(36).slice(2, 8), text: t.text === 'Event Name' ? (eventName || 'Event Name') : t.text, locked: false })));
    setSelected(null);
  };

  const save = async () => {
    if (!event) return;
    setSaving(true);
    try {
      await axios.put(`${API}/events/${event.event_id}/template`, {
        template_config: { photos, texts, bgColor, bgImage, orientation, activePreset, canvasW: CANVAS_W, canvasH: CANVAS_H }
      });
      toast.success('Template saved');
    } catch (e) {
      toast.error('Save failed — please try again');
    }
    finally { setSaving(false); }
  };

  // Auto-save on changes
  const saveTimeout = useRef(null);
  const autoSave = useCallback(() => {
    if (saveTimeout.current) clearTimeout(saveTimeout.current);
    saveTimeout.current = setTimeout(async () => {
      if (!event) return;
      try {
        await axios.put(`${API}/events/${event.event_id}/template`, {
          template_config: { photos, texts, bgColor, bgImage, orientation, activePreset, canvasW: CANVAS_W, canvasH: CANVAS_H }
        });
      } catch {}
    }, 2000);
  }, [event, photos, texts, bgColor, bgImage, orientation, activePreset]);

  useEffect(() => { if (event) autoSave(); }, [photos, texts, bgColor, bgImage, orientation]);

  const addPhoto = () => {
    setPhotos(prev => [...prev, { id: Math.random().toString(36).slice(2, 8), x: 50, y: 50, w: 200, h: 150, locked: false }]);
  };

  const addText = () => {
    setTexts(prev => [...prev, { id: Math.random().toString(36).slice(2, 8), text: 'Your Text', x: CANVAS_W / 2, y: CANVAS_H - 80, fontSize: 24, fontFamily: 'Inter', color: '#FFFFFF', locked: false }]);
  };

  const deleteSelected = () => {
    if (!selected) return;
    if (selected.type === 'photo') setPhotos(prev => prev.filter((_, i) => i !== selected.index));
    else setTexts(prev => prev.filter((_, i) => i !== selected.index));
    setSelected(null);
  };

  const duplicateSelected = () => {
    if (!selected) return;
    if (selected.type === 'photo') {
      const p = photos[selected.index];
      setPhotos(prev => [...prev, { ...p, id: Math.random().toString(36).slice(2, 8), x: p.x + 20, y: p.y + 20 }]);
    } else {
      const t = texts[selected.index];
      setTexts(prev => [...prev, { ...t, id: Math.random().toString(36).slice(2, 8), x: t.x + 20, y: t.y + 20 }]);
    }
  };

  const toggleLock = () => {
    if (!selected) return;
    if (selected.type === 'photo') setPhotos(prev => prev.map((p, i) => i === selected.index ? { ...p, locked: !p.locked } : p));
    else setTexts(prev => prev.map((t, i) => i === selected.index ? { ...t, locked: !t.locked } : t));
  };

  const flipPhotoOrientation = () => {
    if (!selected || selected.type !== 'photo') return;
    setPhotos(prev => prev.map((p, i) => i === selected.index ? { ...p, w: p.h, h: p.w } : p));
  };

  const switchOrientation = (newOrient) => {
    setOrientation(newOrient);
    applyPreset('strip', event?.event_name, newOrient);
  };

  // Scale tracking for Rnd
  const [scale, setScale] = useState(1);
  useEffect(() => {
    const updateScale = () => {
      if (canvasRef.current) setScale(canvasRef.current.getBoundingClientRect().width / CANVAS_W);
    };
    updateScale();
    window.addEventListener('resize', updateScale);
    return () => window.removeEventListener('resize', updateScale);
  }, [CANVAS_W, orientation, activePreset]);

  const updatePhoto = (i, data) => setPhotos(prev => prev.map((p, idx) => idx === i ? { ...p, ...data } : p));
  const updateTextPos = (i, data) => setTexts(prev => prev.map((t, idx) => idx === i ? { ...t, ...data } : t));

  const handleBgUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => setBgImage(ev.target.result);
    reader.readAsDataURL(file);
  };

  // ── Template Background Library ──
  const [library, setLibrary] = useState([]);
  const [libraryOpen, setLibraryOpen] = useState(false);
  const [libCategory, setLibCategory] = useState('all');
  const [previewTpl, setPreviewTpl] = useState(null);
  const [eventPhotos, setEventPhotos] = useState([]);
  useEffect(() => {
    axios.get(`${API}/template-library`).then(r => setLibrary(r.data || [])).catch(() => {});
  }, []);
  // Fetch up to 4 real guest photos for the preview so hosts see their actual event,
  // not "PHOTO 1/2/3" placeholders. Falls back gracefully when there are no uploads yet.
  useEffect(() => {
    if (!event?.event_id) return;
    axios.get(`${API}/events/${event.event_id}/photos`).then(r => {
      const imgs = (r.data || []).filter(p => (p.content_type || '').startsWith('image/')).slice(0, 4);
      setEventPhotos(imgs.map(p => getMediaUrl(p.storage_path, `${API}/events/${event.event_id}/photos/${p.photo_id}`)));
    }).catch(() => {});
  }, [event]);
  const applyPreview = () => {
    if (!previewTpl) return;
    setBgImage(`${API}/template-library/${previewTpl.template_id}/image`);
    toast.success(`Applied "${previewTpl.name}"`);
    setPreviewTpl(null);
    setLibraryOpen(false);
  };

  const updateText = (index, field, value) => {
    setTexts(prev => prev.map((t, i) => i === index ? { ...t, [field]: value } : t));
  };

  const selItem = selected?.type === 'photo' ? photos[selected.index] : selected?.type === 'text' ? texts[selected.index] : null;

  if (!event) return <div className="min-h-screen bg-[#15161B] flex items-center justify-center"><div className="animate-spin rounded-full h-10 w-10 border-4 border-[#B89762]/50 border-t-transparent" /></div>;

  return (
    <div className="min-h-screen flex flex-col" style={{ background: '#0F0F12', fontFamily: 'Inter, sans-serif' }} data-testid="template-editor">
      {/* Header */}
      <div className="flex items-center justify-between px-6 py-4 border-b border-white/10">
        <button onClick={() => navigate(`/event/${slug}/booth`)} className="flex items-center gap-2 text-white/60 hover:text-white transition-colors" data-testid="editor-back">
          <ArrowLeft size={20} weight="bold" /><span className="text-sm font-medium">Back to Booth</span>
        </button>
        <h1 className="text-white font-bold text-lg">Template Editor</h1>
        <button onClick={save} disabled={saving} className="flex items-center gap-2 px-5 py-2 rounded-xl font-bold text-white text-sm transition-all hover:scale-105" style={{ background: 'linear-gradient(135deg, #B89762, #C29D52)' }} data-testid="editor-save">
          <FloppyDisk size={16} weight="bold" />{saving ? 'Saving...' : 'Save'}
        </button>
      </div>

      <div className="flex-1 flex flex-col lg:flex-row">
        {/* CANVAS */}
        <div className="flex-1 flex items-center justify-center p-6">
          <div className="relative" style={{ width: '100%', maxWidth: 500 }}>
            <div
              ref={canvasRef}
              className="relative mx-auto rounded-2xl overflow-hidden"
              style={{
                width: '100%', paddingBottom: `${(CANVAS_H / CANVAS_W) * 100}%`,
                background: bgImage ? `url(${bgImage}) center/cover` : bgColor,
                boxShadow: '0 25px 80px rgba(0,0,0,0.5)',
              }}
              onClick={() => setSelected(null)}
              data-testid="editor-canvas"
            >
              {/* Scaled container */}
              <div className="absolute inset-0" style={{ transform: `scale(${scale})`, transformOrigin: 'top left', width: CANVAS_W, height: CANVAS_H }}>

                {/* Photo slots via Rnd */}
                {photos.map((p, i) => (
                  <Rnd key={p.id}
                    position={{ x: p.x, y: p.y }}
                    size={{ width: p.w, height: p.h }}
                    minWidth={40} minHeight={40}
                    bounds="parent"
                    disableDragging={p.locked}
                    enableResizing={!p.locked}
                    onDragStop={(e, d) => { updatePhoto(i, { x: d.x, y: d.y }); }}
                    onResizeStop={(e, dir, ref, delta, pos) => { updatePhoto(i, { w: parseInt(ref.style.width), h: parseInt(ref.style.height), x: pos.x, y: pos.y }); }}
                    onClick={(e) => { e.stopPropagation(); setSelected({ type: 'photo', index: i }); }}
                    style={{ zIndex: selected?.type === 'photo' && selected.index === i ? 20 : 5 }}
                  >
                    <div className={`w-full h-full flex items-center justify-center rounded-xl ${selected?.type === 'photo' && selected.index === i ? 'ring-2 ring-[#B89762]' : ''}`}
                      style={{ background: 'rgba(0,0,0,0.5)', border: '2px dashed rgba(255,255,255,0.25)', cursor: p.locked ? 'default' : 'move' }}
                      data-testid={`photo-slot-${i}`}>
                      <span className="text-white/40 text-xs font-bold select-none">PHOTO {i + 1}</span>
                      {p.locked && <Lock size={12} weight="bold" className="absolute top-1 right-1 text-white/30" />}
                    </div>
                  </Rnd>
                ))}

                {/* Text blocks via Rnd */}
                {texts.map((t, i) => (
                  <Rnd key={t.id}
                    position={{ x: t.x - 50, y: t.y - 15 }}
                    size={{ width: 'auto', height: 'auto' }}
                    enableResizing={false}
                    disableDragging={t.locked}
                    bounds="parent"
                    onDragStop={(e, d) => { updateTextPos(i, { x: d.x + 50, y: d.y + 15 }); }}
                    onClick={(e) => { e.stopPropagation(); setSelected({ type: 'text', index: i }); }}
                    style={{ zIndex: selected?.type === 'text' && selected.index === i ? 20 : 10 }}
                  >
                    <div className={`px-2 py-1 cursor-move select-none ${selected?.type === 'text' && selected.index === i ? 'outline outline-2 outline-purple-400 outline-offset-2' : 'hover:outline hover:outline-1 hover:outline-white/20 hover:outline-offset-2'}`}
                      style={{ fontFamily: t.fontFamily, fontSize: t.fontSize, color: t.color, fontWeight: 'bold', whiteSpace: 'nowrap', textShadow: '0 2px 8px rgba(0,0,0,0.5)' }}
                      data-testid={`text-block-${i}`}>
                      {t.text}
                    </div>
                  </Rnd>
                ))}

              </div>
            </div>
          </div>
        </div>

        {/* FLOATING EDIT PANEL — shows below canvas when element selected */}
        {selected && selItem && (
          <div className="w-full max-w-xl mx-auto mt-4 px-4" data-testid="element-controls">
            <div className="rounded-2xl p-5" style={{ background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.12)', backdropFilter: 'blur(20px)' }}>
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-white font-bold text-sm">{selected.type === 'photo' ? `Photo Slot ${selected.index + 1}` : 'Text Block'}</h3>
                <div className="flex gap-2">
                  <button onClick={toggleLock} className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-semibold bg-white/10 text-white/60 hover:bg-white/15 transition-colors" data-testid="toggle-lock">
                    {selItem.locked ? <Lock size={12} weight="bold" /> : <LockOpen size={12} weight="bold" />}
                    {selItem.locked ? 'Unlock' : 'Lock'}
                  </button>
                  <button onClick={duplicateSelected} className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-semibold bg-white/10 text-white/60 hover:bg-white/15 transition-colors" data-testid="duplicate-btn">
                    <Copy size={12} weight="bold" /> Copy
                  </button>
                  <button onClick={deleteSelected} className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-semibold bg-red-500/20 text-red-400 hover:bg-red-500/30 transition-colors" data-testid="delete-btn">
                    <Trash size={12} weight="bold" /> Delete
                  </button>
                </div>
              </div>

              {selected.type === 'photo' && (
                <button onClick={flipPhotoOrientation} className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-semibold bg-white/10 text-white/60 hover:bg-white/15 transition-colors" data-testid="flip-photo">
                  <ArrowsOutCardinal size={14} weight="bold" /> Flip Landscape / Portrait
                </button>
              )}

              {selected.type === 'text' && (
                <div className="flex flex-wrap items-end gap-3">
                  <div className="flex-1 min-w-[140px]">
                    <label className="block text-xs text-white/40 font-semibold mb-1">Text</label>
                    <input type="text" value={texts[selected.index]?.text || ''} onChange={e => updateText(selected.index, 'text', e.target.value)}
                      className="w-full px-3 py-2.5 rounded-xl bg-white/10 border border-white/10 text-white text-sm focus:outline-none focus:border-[#B89762]/50" data-testid="text-input" />
                  </div>
                  <div className="w-36">
                    <label className="block text-xs text-white/40 font-semibold mb-1">Font</label>
                    <select value={texts[selected.index]?.fontFamily || 'Inter'} onChange={e => updateText(selected.index, 'fontFamily', e.target.value)}
                      className="w-full px-3 py-2.5 rounded-xl bg-white/10 border border-white/10 text-white text-sm" data-testid="font-select">
                      {FONTS.map(f => <option key={f} value={f} style={{ color: '#000' }}>{f}</option>)}
                    </select>
                  </div>
                  <div className="w-20">
                    <label className="block text-xs text-white/40 font-semibold mb-1">Size</label>
                    <input type="number" value={texts[selected.index]?.fontSize || 24} onChange={e => updateText(selected.index, 'fontSize', parseInt(e.target.value) || 24)} min={10} max={80}
                      className="w-full px-3 py-2.5 rounded-xl bg-white/10 border border-white/10 text-white text-sm" data-testid="font-size" />
                  </div>
                  <div>
                    <label className="block text-xs text-white/40 font-semibold mb-1">Color</label>
                    <input type="color" value={texts[selected.index]?.color || '#FFFFFF'} onChange={e => updateText(selected.index, 'color', e.target.value)}
                      className="w-11 h-11 rounded-xl border-0 cursor-pointer" data-testid="font-color" />
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* BOTTOM TOOLBAR */}
      <div className="border-t border-white/10 px-4 py-4" style={{ background: 'rgba(15,15,18,0.95)', backdropFilter: 'blur(20px)' }}>
        {/* Action buttons */}
        <div className="flex items-center justify-center gap-3 mb-4">
          <button onClick={addPhoto} className="flex items-center gap-2 px-5 py-3 rounded-xl font-semibold text-sm transition-all hover:scale-105" style={{ background: 'linear-gradient(135deg, #B89762, #C29D52)', color: 'white' }} data-testid="add-photo-btn">
            <Camera size={18} weight="bold" /> Add Photo
          </button>
          <button onClick={addText} className="flex items-center gap-2 px-5 py-3 rounded-xl font-semibold text-sm bg-white/10 text-white border border-white/10 hover:bg-white/15 transition-colors" data-testid="add-text-btn">
            <TextT size={18} weight="bold" /> Add Text
          </button>
          <button onClick={() => fileRef.current?.click()} className="flex items-center gap-2 px-5 py-3 rounded-xl font-semibold text-sm bg-white/10 text-white border border-white/10 hover:bg-white/15 transition-colors" data-testid="bg-upload-btn">
            <ImageIcon size={18} weight="bold" /> Background
          </button>
          <button onClick={() => setLibraryOpen(true)} className="flex items-center gap-2 px-5 py-3 rounded-xl font-semibold text-sm text-white border border-white/10 transition-all hover:scale-105" style={{ background: 'linear-gradient(135deg, rgba(255,107,107,0.2), rgba(255,204,0,0.2))' }} data-testid="bg-library-btn">
            <ImageIcon size={18} weight="bold" /> Library
          </button>
          <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={handleBgUpload} />
          <div className="flex items-center gap-2 ml-2">
            <input type="color" value={bgColor} onChange={e => { setBgColor(e.target.value); setBgImage(null); }} className="w-9 h-9 rounded-lg border-0 cursor-pointer" data-testid="bg-color-picker" />
          </div>
          <button onClick={() => setShowPreview(!showPreview)} className="flex items-center gap-2 px-4 py-3 rounded-xl font-semibold text-sm bg-white/10 text-white border border-white/10 hover:bg-white/15 transition-colors" data-testid="preview-toggle">
            <Eye size={18} weight="bold" /> Preview
          </button>
        </div>

        {/* Orientation + Layout presets */}
        <div className="flex items-center justify-center gap-3">
          <div className="flex rounded-xl overflow-hidden border border-white/10 mr-4">
            <button onClick={() => switchOrientation('portrait')}
              className={`px-4 py-2.5 text-sm font-semibold transition-all ${orientation === 'portrait' ? 'bg-[#B89762] text-white' : 'bg-[#15161B] text-white/40 hover:text-white/60'}`}
              data-testid="orient-portrait">
              Portrait
            </button>
            <button onClick={() => switchOrientation('landscape')}
              className={`px-4 py-2.5 text-sm font-semibold transition-all ${orientation === 'landscape' ? 'bg-[#B89762] text-white' : 'bg-[#15161B] text-white/40 hover:text-white/60'}`}
              data-testid="orient-landscape">
              Landscape
            </button>
          </div>
          {[
            { id: 'strip', label: 'Classic Strip' },
            { id: 'grid', label: 'Grid' },
            { id: 'polaroid', label: 'Polaroid' },
            { id: 'fullframe', label: 'Full Frame' },
          ].map(p => (
            <button key={p.id} onClick={() => applyPreset(p.id, event?.event_name, orientation)}
              className="px-5 py-2.5 rounded-xl text-sm font-semibold transition-all hover:scale-105 bg-[#15161B] border border-white/10 text-white/60 hover:text-white hover:bg-white/10"
              data-testid={`preset-${p.id}`}>
              {p.label}
            </button>
          ))}
        </div>
      </div>

      {/* BACKGROUND LIBRARY MODAL */}
      {libraryOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.75)', backdropFilter: 'blur(8px)' }} onClick={() => setLibraryOpen(false)} data-testid="library-modal">
          <div className="rounded-2xl max-w-3xl w-full max-h-[85vh] overflow-hidden flex flex-col" style={{ background: '#141416', border: '1px solid rgba(255,255,255,0.12)' }} onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-6 py-4 border-b border-white/10">
              <div>
                <h3 className="text-white font-bold text-lg">Background Library</h3>
                <p className="text-white/50 text-xs mt-0.5">Pick a themed background — or tap Background in the toolbar to upload your own.</p>
              </div>
              <button onClick={() => setLibraryOpen(false)} className="p-2 rounded-full hover:bg-white/10 text-white/60" data-testid="library-close"><span className="text-lg leading-none">×</span></button>
            </div>
            <div className="flex-1 overflow-y-auto p-5">
              {library.length === 0 ? (
                <div className="py-16 text-center">
                  <p className="text-white/40 text-sm">No background templates yet.</p>
                  <p className="text-white/30 text-xs mt-2">Admins can upload via the Admin Panel → Template Library tab.</p>
                </div>
              ) : (
                <div>
                  {/* Category filter chips */}
                  {(() => {
                    const cats = ['all', ...Array.from(new Set(library.map(t => t.category))).sort()];
                    return (
                      <div className="flex gap-2 overflow-x-auto pb-3 mb-4" style={{ scrollbarWidth: 'none' }} data-testid="editor-library-chips">
                        {cats.map(c => {
                          const active = libCategory === c;
                          const count = c === 'all' ? library.length : library.filter(t => t.category === c).length;
                          return (
                            <button key={c} onClick={() => setLibCategory(c)}
                              className="flex-shrink-0 px-4 py-2 rounded-full text-xs font-bold uppercase tracking-wider transition-all whitespace-nowrap"
                              style={active
                                ? { background: 'linear-gradient(135deg, #FF6B6B, #FFCC00)', color: '#141416' }
                                : { background: 'rgba(255,255,255,0.06)', color: 'rgba(255,255,255,0.55)', border: '1px solid rgba(255,255,255,0.08)' }}
                              data-testid={`editor-cat-${c}`}>
                              {c} · {count}
                            </button>
                          );
                        })}
                      </div>
                    );
                  })()}
                  {(libCategory === 'all'
                    ? Array.from(new Set(library.map(t => t.category))).sort()
                    : [libCategory]
                  ).map(cat => (
                    <div key={cat} className="mb-6">
                      {libCategory === 'all' && <h4 className="text-white/50 text-xs font-bold uppercase tracking-wider mb-3">{cat}</h4>}
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                        {library.filter(t => t.category === cat).map(t => (
                          <button key={t.template_id} onClick={() => setPreviewTpl(t)}
                            className="group relative rounded-xl overflow-hidden aspect-square transition-all hover:scale-[1.03] hover:ring-2 hover:ring-[#B89762]"
                            style={{ background: '#0F0F10', border: '1px solid rgba(255,255,255,0.08)' }}
                            data-testid={`library-item-${t.template_id}`}>
                            <img src={`${API}/template-library/${t.template_id}/image`} alt={t.name}
                              className="w-full h-full object-cover" loading="lazy" />
                            <div className="absolute inset-x-0 bottom-0 px-3 py-2 text-left" style={{ background: 'linear-gradient(to top, rgba(0,0,0,0.85), transparent)' }}>
                              <p className="text-white text-xs font-semibold truncate">{t.name}</p>
                              <p className="text-white/60 text-[10px] mt-0.5 opacity-0 group-hover:opacity-100 transition-opacity">Tap to preview →</p>
                            </div>
                          </button>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* PREVIEW MODAL — see the template applied to your actual event canvas before committing */}
      {previewTpl && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.85)', backdropFilter: 'blur(12px)' }} onClick={() => setPreviewTpl(null)} data-testid="preview-modal">
          <div className="rounded-2xl max-w-lg w-full overflow-hidden flex flex-col" style={{ background: '#141416', border: '1px solid rgba(255,255,255,0.12)' }} onClick={e => e.stopPropagation()}>
            <div className="px-5 py-4 border-b border-white/10">
              <p className="text-white/40 text-[10px] font-bold uppercase tracking-wider">{previewTpl.category} · Preview</p>
              <h3 className="text-white font-bold text-lg">{previewTpl.name}</h3>
            </div>
            <div className="p-5 flex items-center justify-center" style={{ background: '#0A0A0B' }}>
              <div className="relative rounded-xl overflow-hidden" style={{
                width: 280, height: 280 * (CANVAS_H / CANVAS_W),
                background: `url(${API}/template-library/${previewTpl.template_id}/image) center/cover`,
                boxShadow: '0 20px 60px rgba(0,0,0,0.6)',
              }}>
                <div className="absolute inset-0" style={{ transform: `scale(${280 / CANVAS_W})`, transformOrigin: 'top left', width: CANVAS_W, height: CANVAS_H }}>
                  {photos.map((p, i) => (
                    <div key={p.id} className="absolute rounded-xl overflow-hidden"
                      style={{ left: p.x, top: p.y, width: p.w, height: p.h,
                        background: 'rgba(0,0,0,0.35)', border: '2px dashed rgba(255,255,255,0.3)' }}>
                      {eventPhotos[i] ? (
                        <img src={eventPhotos[i]} alt="" className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <span className="text-white/50 text-xs font-bold select-none">PHOTO {i + 1}</span>
                        </div>
                      )}
                    </div>
                  ))}
                  {texts.map((t) => (
                    <div key={t.id} className="absolute px-2 py-1 select-none"
                      style={{ left: t.x - 50, top: t.y - 15, fontFamily: t.fontFamily, fontSize: t.fontSize,
                        color: t.color, fontWeight: 'bold', whiteSpace: 'nowrap', textShadow: '0 2px 8px rgba(0,0,0,0.5)' }}>
                      {t.text}
                    </div>
                  ))}
                  {/* Watermark preview */}
                  <div className="absolute right-2 bottom-2 px-2 py-0.5 rounded-md text-white/80 font-bold"
                    style={{ background: 'rgba(0,0,0,0.45)', fontSize: 11 }}>
                    Powered by EventDrop.co
                  </div>
                </div>
              </div>
            </div>
            <div className="p-5 pt-2">
              <p className="text-white/40 text-xs text-center mb-4">Your actual photos will drop into the slots shown above.</p>
              <div className="flex gap-3">
                <button onClick={() => setPreviewTpl(null)}
                  className="flex-1 py-3 rounded-xl font-bold text-sm text-white/70 hover:text-white border border-white/10 hover:bg-white/5 transition-all"
                  data-testid="preview-back">Back</button>
                <button onClick={applyPreview}
                  className="flex-1 py-3 rounded-xl font-bold text-sm text-white transition-all hover:scale-[1.02]"
                  style={{ background: 'linear-gradient(135deg, #FF6B6B 0%, #FFCC00 100%)' }}
                  data-testid="preview-apply">Apply to my event</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TemplateEditor;
